import {
  Box,
  Card,
  CardContent,
  Grid,
  Icon,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { api_request } from "../../store/actions/commonAction";
import { useDispatch, useSelector } from "react-redux";
import {
  API_METHOD_GET,
  API_METHOD_POST,
  API_METHOD_PUT,
  ENTITY_STATUS,
} from "../../config/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import CoreButton from "../../core/inputs/CoreButton";
import CoreIconButton from "../../core/inputs/CoreIconButton";
import {
  DELETE_TABLE_DATA_ERROR,
  DELETE_TABLE_DATA_SUCCESS,
  FETCH_TABLE_DATA_ERROR,
  FETCH_TABLE_DATA_SEARCH_SUCCESS,
  FETCH_TABLE_DATA_SUCCESS,
  UPDATE_TABLE_DATA_ERROR,
  UPDATE_TABLE_DATA_SUCCESS,
} from "../../store/types/manageDataTypes";
import Swal from "sweetalert2";
import { theme } from "../../config/theme";
import { setManageDataReducer } from "../../store/actions/manageDataAction";
import MasterDataEditModal from "./MasterDataEditModal";
import MasterDataAddModal from "./MasterDataAddModal";
import AddMasterDataJsonModal from "./AddMasterDataJsonModal";
import CoreButtonContainer from "../../core/layouts/CoreButtonContainer";

export default function ManageMasterData() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const masterData = useSelector((state) => state?.manageData?.tableData);

  const updateTableDataSuccess = useSelector(
    (state) => state?.manageData?.updateTableDataSuccess,
  );
  const updateTableDataError = useSelector(
    (state) => state?.manageData?.updateTableDataError,
  );
  const updateTableDataMsg = useSelector(
    (state) => state?.manageData?.updateTableDataMsg,
  );

  const insertTableDataSuccess = useSelector(
    (state) => state?.manageData?.insertTableDataSuccess,
  );
  const insertTableDataError = useSelector(
    (state) => state?.manageData?.insertTableDataError,
  );
  const insertTableDataMsg = useSelector(
    (state) => state?.manageData?.insertTableDataMsg,
  );

  const deleteTableDataSuccess = useSelector(
    (state) => state?.manageData?.deleteTableDataSuccess,
  );
  const deleteTableDataError = useSelector(
    (state) => state?.manageData?.deleteTableDataError,
  );
  const deleteTableDataMsg = useSelector(
    (state) => state?.manageData?.deleteTableDataMsg,
  );

  const parent = searchParams.get("parent");
  const columns = [
    "id",
    "name",
    "label",
    "order",
    "extraInfo",
    "_status",
    "parentId",
    "value",
  ];
  const [showMasterDataJsonAddModal, setShowMasterDataJsonAddModal] =
    useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [editItemDetail, setEditItemDetail] = useState(null);

  useEffect(() => {
    getMasterData(parent);
  }, []);

  useEffect(() => {
    if (parent) getMasterData(parent);
  }, [parent]);

  useEffect(() => {
    if (updateTableDataSuccess) {
      setShowEditModal(false);
      dispatch(setManageDataReducer({ updateTableDataSuccess: false }));
      getMasterData(parent);
    }
    if (updateTableDataError) {
      setShowEditModal(false);
      Swal.fire({
        title: "Error",
        text: updateTableDataMsg,
        icon: "error",
        confirmButtonColor: theme.palette.primary.main,
      });
      dispatch(setManageDataReducer({ updateTableDataError: false }));
    }
  }, [updateTableDataSuccess, updateTableDataError]);

  useEffect(() => {
    if (insertTableDataSuccess) {
      setShowAddModal(false);
      dispatch(setManageDataReducer({ insertTableDataSuccess: false }));
      getMasterData(parent);
      Swal.fire({
        title: "Success",
        text: insertTableDataMsg,
        icon: "success",
        confirmButtonColor: theme.palette.primary.main,
      });
    }
    if (insertTableDataError) {
      dispatch(setManageDataReducer({ insertTableDataError: false }));
      Swal.fire({
        title: "Oops!",
        text: insertTableDataMsg,
        icon: "error",
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  }, [insertTableDataSuccess, insertTableDataError]);

  useEffect(() => {
    if (deleteTableDataSuccess) {
      dispatch(setManageDataReducer({ deleteTableDataSuccess: false }));
      Swal.fire({
        title: "Success",
        text: deleteTableDataMsg,
        icon: "success",
        confirmButtonColor: theme.palette.primary.main,
      });
      getMasterData(parent);
    }
    if (deleteTableDataError) {
      dispatch(setManageDataReducer({ deleteTableDataError: false }));
      Swal.fire({
        title: "Oops!",
        text: deleteTableDataMsg,
        icon: "error",
        confirmButtonColor: theme.palette.primary.main,
      });
    }
  }, [deleteTableDataSuccess, deleteTableDataError]);

  const getMasterData = (parentId) => {
    dispatch(
      api_request(
        API_METHOD_GET,
        parentId
          ? `/data/MasterData?parentId=${Number(parentId)}`
          : `/data/MasterData`,
        {},
        {},
        null,
        FETCH_TABLE_DATA_SUCCESS,
        FETCH_TABLE_DATA_ERROR,
        true,
        false,
        true,
      ),
    );
  };

  const updateRow = (id, body) => {
    dispatch(
      api_request(
        API_METHOD_PUT,
        `/data/MasterData/${id}/update`,
        body,
        null,
        null,
        UPDATE_TABLE_DATA_SUCCESS,
        UPDATE_TABLE_DATA_ERROR,
        true,
        false,
        true,
      ),
    );
  };

  const onEdit = (value) => {
    updateRow(editItemDetail.id, { [editItemDetail.col]: value });
  };

  const onDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "If you proceed, the item will be permanently deleted from the database. If you prefer a soft delete, please update its _status field to 'delete' instead.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primary.main,
      cancelButtonColor: theme.palette.error.main,
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(
          api_request(
            API_METHOD_POST,
            "/data/MasterData/delete",
            { tableIds: [id] },
            null,
            null,
            DELETE_TABLE_DATA_SUCCESS,
            DELETE_TABLE_DATA_ERROR,
            true,
            false,
            true,
          ),
        );
      }
    });
  };

  return (
    <Box style={{ width: "100%" }}>
      <Box style={{ padding: "16px" }}>
        <AddMasterDataJsonModal
          show={showMasterDataJsonAddModal}
          onClose={() => {
            setShowMasterDataJsonAddModal(false);
          }}
        />
        <MasterDataEditModal
          show={showEditModal}
          col={editItemDetail?.col}
          asyncSelectDetail={{
            display: (item) => {
              return { value: item.id, label: item.name + "|" + item.id };
            },
            valueColumn: "id",
            reducerVar: "searchResults",
            apiPath: "/data/MasterData",
            query: {},
            loadType: null,
            successType: FETCH_TABLE_DATA_SEARCH_SUCCESS,
            errorType: null,
            loading: true,
          }}
          value={
            editItemDetail &&
            masterData?.find((f) => f.id === editItemDetail?.id) &&
            masterData?.find((f) => f.id === editItemDetail?.id)[
              editItemDetail?.col
            ]
          }
          options={
            editItemDetail?.col === "_status"
              ? Object.keys(ENTITY_STATUS).map((item) => {
                  return {
                    value: ENTITY_STATUS[item],
                    label: ENTITY_STATUS[item],
                  };
                })
              : []
          }
          onClose={() => {
            setShowEditModal(false);
          }}
          onSubmit={onEdit}
        />
        <MasterDataAddModal
          show={showAddModal}
          parentId={parent}
          onClose={() => {
            setShowAddModal(false);
          }}
        />
        <div>
          <CoreButtonContainer style={{ marginTop: 0, marginBottom: 12 }}>
            <CoreButton
              onClick={() => {
                setShowAddModal(true);
              }}
            >
              Add
            </CoreButton>
            <CoreButton
              style={{ marginLeft: 12 }}
              onClick={() => {
                setShowMasterDataJsonAddModal(true);
              }}
            >
              Add JSON
            </CoreButton>
          </CoreButtonContainer>
          <div>
            <TableContainer style={{ height: "70vh", width: "100%" }}>
              <Paper>
                {masterData?.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ textAlign: "center" }}>
                          Action
                        </TableCell>
                        {columns
                          // .sort((a, b) => (a > b ? 1 : -1))
                          .map((item, index) => (
                            <TableCell key={index}>{item}</TableCell>
                          ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...masterData]
                        .sort((a, b) => (a?.id > b?.id ? 1 : -1))
                        .map((item) => (
                          <TableRow key={item.id}>
                            <TableCell style={{ textAlign: "center" }}>
                              <Box display="flex" gap={1}>
                                <Tooltip title="Delete">
                                  <CoreIconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      onDelete(item.id);
                                    }}
                                  >
                                    <Icon style={{ fontSize: "20px" }}>
                                      delete
                                    </Icon>
                                  </CoreIconButton>
                                </Tooltip>
                                <Tooltip title="Open children">
                                  <CoreIconButton
                                    size="small"
                                    color="secondery"
                                    onClick={(event) => {
                                      navigate(
                                        "/manage/masterData?parent=" + item.id,
                                      );
                                    }}
                                  >
                                    <Icon style={{ fontSize: "20px" }}>
                                      open_in_new
                                    </Icon>
                                  </CoreIconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            {columns
                              // .sort((a, b) => (a > b ? 1 : -1))
                              .map((col, colIndex) => (
                                <TableCell
                                  key={item.id + colIndex}
                                  sx={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: 200, // set a max width as needed
                                  }}
                                  onClick={() => {
                                    if (col !== "id") {
                                      setEditItemDetail({
                                        id: item.id,
                                        col: col,
                                      });
                                      setShowEditModal(true);
                                    }
                                  }}
                                >
                                  {typeof item[col] !== "object"
                                    ? item[col]
                                    : JSON.stringify(item[col])}
                                </TableCell>
                              ))}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                )}
              </Paper>
            </TableContainer>
          </div>
        </div>
      </Box>
    </Box>
  );
}
